import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useGamificationContext } from '../context/GamificationContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import { FiSearch, FiTarget, FiTrendingUp } from 'react-icons/fi';
import { getPositionColor } from '../lib/utils';
import { techniques as baseTechniques } from '../data/techniques';

const TechniqueLibrary = () => {
  const { calculateTechniqueMastery } = useGamificationContext();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPosition, setSelectedPosition] = useState('All');
  const [selectedType, setSelectedType] = useState('All');
  const [selectedDifficulty, setSelectedDifficulty] = useState('All');

  // Enhance techniques with mastery data
  const techniques = useMemo(() => {
    return baseTechniques.map(tech => {
      const mastery = calculateTechniqueMastery(tech.name);
      return {
        ...tech,
        ...mastery, // adds level, title, progress, count, successRate
      };
    });
  }, [calculateTechniqueMastery]);

  const positions = [
    'All',
    'Guard',
    'Mount',
    'Side Control',
    'Back Control',
    'Half Guard',
    'Standing',
    'Ashi Garami',
    'Front Headlock',
    'Knee on Belly',
    'Leg Entanglements',
    'North-South'
  ];

  const types = [
    'All',
    'Submission',
    'Sweep',
    'Takedown',
    'Guard Pass',
    'Guard Retention',
    'Escape',
    'Back Take',
    'Control'
  ];

  const difficulties = ['All', 'Beginner', 'Intermediate', 'Advanced'];

  const filteredTechniques = techniques.filter((technique) => {
    const matchesSearch = technique.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPosition = selectedPosition === 'All' || technique.position === selectedPosition;
    const matchesType = selectedType === 'All' || technique.type === selectedType;
    const matchesDifficulty = selectedDifficulty === 'All' || technique.difficulty === selectedDifficulty;
    return matchesSearch && matchesPosition && matchesType && matchesDifficulty;
  });

  const getMasteryColor = (level) => {
    switch(level) {
      case 4: return 'text-slate-900 border-slate-900 bg-slate-100 dark:text-slate-200 dark:border-slate-600 dark:bg-slate-800'; // Master (Black)
      case 3: return 'text-amber-800 border-amber-800 bg-amber-50 dark:text-amber-400 dark:border-amber-700 dark:bg-amber-900/30'; // Expert (Brown)
      case 2: return 'text-purple-700 border-purple-700 bg-purple-50 dark:text-purple-400 dark:border-purple-700 dark:bg-purple-900/30'; // Practitioner (Purple)
      case 1: return 'text-blue-700 border-blue-700 bg-blue-50 dark:text-blue-400 dark:border-blue-700 dark:bg-blue-900/30'; // Apprentice (Blue)
      default: return 'text-slate-500 border-slate-300 bg-white dark:text-slate-400 dark:border-slate-600 dark:bg-slate-800'; // Novice (White)
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
      <div className="mb-6 sm:mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mb-2">
              Technique Library
            </h1>
            <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400">
              Track mastery and build your game.
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4 sm:p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <div className="relative">
              <FiSearch className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 dark:text-slate-500" />
              <Input
                placeholder="Search techniques..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <select
              value={selectedPosition}
              onChange={(e) => setSelectedPosition(e.target.value)}
              className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-slate-800 dark:border-slate-700 dark:text-white"
            >
              {positions.map((position) => (
                <option key={position} value={position}>
                  {position === 'All' ? 'All Positions' : position}
                </option>
              ))}
            </select>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-slate-800 dark:border-slate-700 dark:text-white"
            >
              {types.map((type) => (
                <option key={type} value={type}>
                  {type === 'All' ? 'All Types' : type}
                </option>
              ))}
            </select>
            <select
              value={selectedDifficulty}
              onChange={(e) => setSelectedDifficulty(e.target.value)}
              className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-slate-800 dark:border-slate-700 dark:text-white"
            >
              {difficulties.map((difficulty) => (
                <option key={difficulty} value={difficulty}>
                  {difficulty === 'All' ? 'All Difficulties' : difficulty}
                </option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {filteredTechniques.map((technique) => (
          <Link key={technique.id} to={`/techniques/${technique.id}`}>
            <Card className="hover:shadow-md transition-shadow cursor-pointer h-full flex flex-col">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1">
                    <CardTitle className="text-base sm:text-lg break-words dark:text-white">
                      {technique.name}
                    </CardTitle>
                    <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 mt-1">
                      {technique.position}
                    </p>
                  </div>
                  <div className={`px-2 py-1 text-xs font-semibold rounded border ${getMasteryColor(technique.level)}`}>
                    {technique.title}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col justify-between pt-2">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="px-2 py-1 text-xs rounded-full bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                      {technique.type}
                    </span>
                    <div
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: getPositionColor(technique.position) }}
                    />
                  </div>
                  
                  {/* Mastery Progress Bar */}
                  <div className="space-y-1">
                    <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400">
                      <span>Mastery Progress</span>
                      <span>{Math.round(technique.progress)}%</span>
                    </div>
                    <div className="w-full bg-slate-100 dark:bg-slate-700 rounded-full h-1.5 overflow-hidden">
                       <div 
                         className="bg-green-500 h-full rounded-full transition-all duration-300"
                         style={{ width: `${technique.progress}%` }}
                       />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-xs text-slate-500 dark:text-slate-400 pt-2 border-t border-slate-50 dark:border-slate-800/50">
                    <div className="flex items-center">
                      <FiTarget className="mr-1.5" /> {technique.count} uses
                    </div>
                    <div className="flex items-center">
                      <FiTrendingUp className="mr-1.5" /> {Math.round(technique.successRate)}% rate
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default TechniqueLibrary;